import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ClerkProvider, ClerkFailed, ClerkLoading, SignedIn, SignedOut, SignIn } from '@clerk/clerk-react';
import { App } from './App';
import { Lockup, ModelPropSignoff } from './components/Lockup';
import './theme/global.css';

const root = document.getElementById('root');
if (!root) throw new Error('#root not found');

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

function MissingClerkConfiguration() {
  return <main style={{ padding: 48, fontFamily: 'sans-serif' }}>Dashboard sign-in is not configured.</main>;
}

function AuthShell({ children }: { children: React.ReactNode }) {
  return <main className="vp-auth-shell">{children}</main>;
}

const signInAppearance = {
  variables: {
    colorPrimary: '#094074',
    colorText: '#0b1220',
    colorTextSecondary: '#526071',
    colorBackground: '#ffffff',
    borderRadius: '12px',
    fontFamily: "Questrial, ui-sans-serif, system-ui, sans-serif",
  },
  elements: {
    rootBox: { width: '100%', maxWidth: '420px' },
    cardBox: { width: '100%', boxShadow: '0 16px 48px rgba(9,64,116,0.14)' },
    card: { width: '100%' },
    // Clerk's icon + text error row looked visually off-centre at normal and
    // enlarged browser zoom. Keep the error readable and balanced, rather than
    // making a sign-in failure feel like a broken ModelProp page.
    formFieldError: { justifyContent: 'center', textAlign: 'center' },
    formFieldErrorText: { textAlign: 'center', flex: '0 1 auto' },
    footer: { display: 'none' },
  },
};

createRoot(root).render(
  <StrictMode>
    {publishableKey ? (
      <ClerkProvider publishableKey={publishableKey}>
        <ClerkLoading><AuthShell>Loading secure sign-in…</AuthShell></ClerkLoading>
        <ClerkFailed><AuthShell>Sign-in could not start. Please refresh, then contact ModelProp support if it persists.</AuthShell></ClerkFailed>
        <SignedIn><App /></SignedIn>
        <SignedOut>
          <AuthShell>
            <div className="vp-auth-card">
              <Lockup size="lg" />
              <SignIn appearance={signInAppearance} forceRedirectUrl="/dashboard/" />
              <p>Invite-only access. Need an invitation? Contact <a href="mailto:support@modelprop.ai">ModelProp Support</a>.</p>
              <ModelPropSignoff />
            </div>
          </AuthShell>
        </SignedOut>
      </ClerkProvider>
    ) : <MissingClerkConfiguration />}
  </StrictMode>,
);
